import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Alert, AlertTitle, AlertDescription } from './ui/alert';
import { Cpu, Loader2, CheckCircle2, AlertTriangle, Copy, Activity, Clock, Wifi, Download } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const getAuthHeaders = () => {
  const token = (() => { try { return localStorage.getItem('auth_token'); } catch { return null; } })();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * RegisterDataloggerPanel — user-facing provisioning + device list for DIY
 * dataloggers (Raspberry Pi + RS485/CAN HAT, ESP32-OBD2, etc.).
 *
 * Flow:
 *   1. User enters device_serial (any unique string they choose, e.g. their
 *      Pi's MAC last-6 or a name they pick) + activation_code (4+ char secret
 *      they invent — gets stored, used by the device on every boot).
 *   2. POST /api/datalogger/provision returns a device_jwt.
 *   3. User copies the device_jwt + sets it as DEVICE_TOKEN on their Pi.
 *   4. Pi POSTs CSV chunks to /api/datalogger/upload with the JWT in
 *      Authorization: Bearer <device_jwt>.
 *
 * If the same device_serial is provisioned again, the JWT rotates (jti).
 */
export default function RegisterDataloggerPanel({ user, vehicles }) {
  const [serial, setSerial] = useState('');
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [vehicleId, setVehicleId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [devices, setDevices] = useState([]);
  const [copied, setCopied] = useState(false);

  const loadDevices = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/datalogger/my-devices`, {
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        const body = await res.json();
        setDevices(Array.isArray(body) ? body : (body?.devices || []));
      }
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    if (user?.email) loadDevices();
  }, [user, loadDevices]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const res = await fetch(`${API_URL}/api/datalogger/provision`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          device_serial: serial.trim(),
          activation_code: code.trim(),
          customer_email: user?.email,
          vehicle_id: vehicleId || null,
          device_name: name.trim() || null,
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(body?.detail || `HTTP ${res.status}`);
      } else {
        setResult(body);
        loadDevices();
      }
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setBusy(false);
    }
  };

  const copyJwt = () => {
    if (!result?.device_jwt) return;
    try {
      navigator.clipboard.writeText(result.device_jwt);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* unsupported */ }
  };

  if (!user) {
    return (
      <Card className="bg-[#111111] border-[#27272A] rounded-sm">
        <CardContent className="p-6 text-center">
          <p className="text-sm text-zinc-400">
            Sign in first — datalogger registration is account-bound.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="register-datalogger-panel">
      <Card className="bg-[#111111] border-[#27272A] rounded-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-white font-['Outfit'] text-base">
            <Cpu className="w-5 h-5 text-[#FF5E1A]" />Register a Datalogger
          </CardTitle>
          <CardDescription className="text-zinc-500 text-xs">
            For DIY rigs (Raspberry Pi + RS485/CAN HAT, ESP32-OBD2, etc.). After registering,
            copy the device token onto your hardware — it's used to authenticate every
            CSV upload from the device.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-4" data-testid="datalogger-register-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-wider mb-1.5 block">
                  Device Serial *
                </Label>
                <Input
                  type="text"
                  value={serial}
                  onChange={(e) => setSerial(e.target.value)}
                  placeholder="e.g. PI4-AABBCCDDEEFF or RaspiCAN-001"
                  required
                  className="bg-[#050505] border-[#27272A] text-white text-sm rounded-sm font-['JetBrains_Mono']"
                  data-testid="datalogger-serial-input"
                />
                <p className="text-[10px] text-zinc-600 mt-1">
                  Any unique identifier — your Pi's MAC, hostname, or a name you pick (min 6 chars).
                </p>
              </div>
              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-wider mb-1.5 block">
                  Activation Code *
                </Label>
                <Input
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="Pick a 4+ char secret"
                  required
                  minLength={4}
                  className="bg-[#050505] border-[#27272A] text-white text-sm rounded-sm font-['JetBrains_Mono']"
                  data-testid="datalogger-code-input"
                />
                <p className="text-[10px] text-zinc-600 mt-1">
                  Stored on the device. Treat it like a password — keep it private.
                </p>
              </div>
              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-wider mb-1.5 block">
                  Device Name (optional)
                </Label>
                <Input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Garage Pi"
                  className="bg-[#050505] border-[#27272A] text-white text-sm rounded-sm"
                  data-testid="datalogger-name-input"
                />
              </div>
              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-wider mb-1.5 block">
                  Vehicle (optional)
                </Label>
                <select
                  value={vehicleId}
                  onChange={(e) => setVehicleId(e.target.value)}
                  className="w-full bg-[#050505] border border-[#27272A] text-white text-sm rounded-sm p-2 font-['JetBrains_Mono']"
                  data-testid="datalogger-vehicle-select"
                >
                  <option value="">— Not bound —</option>
                  {(vehicles || []).map(v => (
                    <option key={v.id} value={v.id}>{v.year || ''} {v.make || ''} {v.model || ''}</option>
                  ))}
                </select>
              </div>
            </div>

            {error && (
              <Alert className="bg-red-500/10 border-red-500/30 rounded-sm" data-testid="datalogger-register-error">
                <AlertTriangle className="h-4 w-4 text-red-400" />
                <AlertTitle className="text-white font-['Outfit']">Registration failed</AlertTitle>
                <AlertDescription className="text-zinc-400 text-xs">{error}</AlertDescription>
              </Alert>
            )}

            <Button
              type="submit"
              disabled={busy || !serial || !code}
              className="bg-gradient-to-r from-[#FF5E1A] to-amber-500 hover:from-[#FF7A3D] hover:to-amber-400 text-white text-xs font-bold rounded-sm shadow-[0_0_16px_rgba(255,94,26,0.4)]"
              data-testid="datalogger-register-submit"
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Cpu className="w-3.5 h-3.5 mr-1.5" />}
              {busy ? 'Provisioning…' : 'Register Device'}
            </Button>
          </form>

          {result && (
            <Alert className="mt-5 bg-emerald-500/10 border-emerald-500/30 rounded-sm" data-testid="datalogger-register-success">
              <CheckCircle2 className="h-4 w-4 text-emerald-400" />
              <AlertTitle className="text-white font-['Outfit']">
                {result.rotated ? 'Token rotated' : 'Device registered'}
              </AlertTitle>
              <AlertDescription className="text-zinc-400 text-xs space-y-3">
                <p>
                  Device ID: <span className="text-white font-['JetBrains_Mono']">{result.device_id}</span>
                  {result.expires_in_days && <> · expires in {result.expires_in_days} days</>}
                </p>
                <div>
                  <p className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Device Token (copy onto your hardware)</p>
                  <div className="flex items-stretch gap-0">
                    <code className="flex-1 px-2 py-2 bg-[#050505] border border-emerald-500/30 text-emerald-300 text-[10px] font-['JetBrains_Mono'] rounded-l-sm break-all overflow-hidden text-ellipsis">
                      {result.device_jwt}
                    </code>
                    <Button
                      type="button"
                      onClick={copyJwt}
                      className="bg-emerald-500/20 border border-emerald-500/40 hover:bg-emerald-500/30 text-emerald-300 rounded-l-none rounded-r-sm h-auto text-[10px] px-3"
                      data-testid="datalogger-copy-jwt"
                    >
                      <Copy className="w-3 h-3 mr-1" />{copied ? 'Copied' : 'Copy'}
                    </Button>
                  </div>
                </div>
                <details className="text-[10px] text-zinc-500">
                  <summary className="cursor-pointer text-zinc-400 hover:text-white">Pi setup snippet</summary>
                  <pre className="mt-2 p-2 bg-[#050505] border border-[#27272A] rounded-sm text-[10px] font-['JetBrains_Mono'] text-zinc-300 overflow-x-auto whitespace-pre-wrap break-all">{`# On the Pi, save the token (replace XXXX with the token above)
sudo bash -c 'echo "DEVICE_TOKEN=XXXX" >> /etc/datalogger.env'

# Then upload a CSV (after recording with candump → CSV):
curl -X POST ${API_URL}/api/datalogger/upload \\
  -H "Authorization: Bearer XXXX" \\
  -F "file=@/path/to/log.csv"`}</pre>
                </details>
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Existing devices */}
      <Card className="bg-[#111111] border-[#27272A] rounded-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-white font-['Outfit'] text-base">
            <Activity className="w-5 h-5 text-[#FF5E1A]" />My Dataloggers
            <span className="text-[10px] text-zinc-500 font-['JetBrains_Mono'] ml-auto">{devices.length} registered</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {devices.length === 0 ? (
            <p className="text-xs text-zinc-500 text-center py-4">No devices yet. Register one above.</p>
          ) : (
            <div className="space-y-2">
              {devices.map((d) => (
                <div key={d.device_id} className="p-3 bg-[#050505] border border-[#27272A] rounded-sm" data-testid={`device-row-${d.device_id}`}>
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="min-w-0">
                      <p className="text-sm text-white font-['Outfit']">{d.device_name || 'Smart Datalogger'}</p>
                      <p className="text-[10px] text-zinc-500 font-['JetBrains_Mono']">
                        {d.device_serial} · {d.device_id}
                      </p>
                    </div>
                    <div className="flex items-center gap-3 text-[10px] text-zinc-500 font-['JetBrains_Mono']">
                      <span className="inline-flex items-center gap-1" title="Trips uploaded">
                        <Wifi className="w-3 h-3" />{d.trip_count || 0}
                      </span>
                      <span className="inline-flex items-center gap-1" title="Last seen">
                        <Clock className="w-3 h-3" />{d.last_seen ? new Date(d.last_seen).toLocaleString() : 'never'}
                      </span>
                      <span className={`px-1.5 py-0.5 rounded-sm border text-[9px] uppercase tracking-wider ${
                        d.status === 'active' ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30'
                          : 'bg-zinc-700/40 text-zinc-400 border-zinc-600/40'
                      }`}>
                        {d.status || 'unknown'}
                      </span>
                      <button
                        type="button"
                        onClick={async () => {
                          try {
                            const res = await fetch(`${API_URL}/api/datalogger/uploader-script/${d.device_id}`, {
                              headers: getAuthHeaders(),
                            });
                            if (!res.ok) {
                              alert(`Could not fetch script: HTTP ${res.status}`);
                              return;
                            }
                            const text = await res.text();
                            const blob = new Blob([text], { type: 'text/x-python' });
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = `pi_datalogger_${d.device_id}.py`;
                            document.body.appendChild(a);
                            a.click();
                            a.remove();
                            URL.revokeObjectURL(url);
                          } catch (err) {
                            alert(`Download failed: ${err.message || err}`);
                          }
                        }}
                        className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#FF5E1A]/10 border border-[#FF5E1A]/30 text-[#FF5E1A] hover:bg-[#FF5E1A]/20 rounded-sm text-[9px] uppercase tracking-wider"
                        data-testid={`download-uploader-${d.device_id}`}
                        title="Download the Pi-side Python uploader script"
                      >
                        <Download className="w-3 h-3" />Pi Script
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
